import { BeconAccountTypes, BeconScalpTypes, BeconUserTypes } from "@becon-team/becon-fe-lib/my-types";
import { zDateString } from "@becon-team/becon-fe-lib/my-zod";
import { GroupUnitEnum, OrderByEnum } from "api/@skin/skin.dto";
import { ComparisonCandidateDtoSchema } from "modules/scalp/services/scalp.dto";
import { z } from "zod";

// # V1: Before&After (lite)
export enum ComparisonV1SummaryItemEnum {
  SCALP_HEALTH_SCORE = "SCALP_HEALTH_SCORE",
  HAIR_FOLLICLE_DENSITY = BeconScalpTypes.HairLossItemEnum.HAIR_FOLLICLE_DENSITY,
  HAIR_QUANTITY = BeconScalpTypes.HairLossItemEnum.HAIR_QUANTITY,
  DEAD_SKIN = BeconScalpTypes.ScalpImageItemEnum.DEAD_SKIN,
  SENSITIVITY = BeconScalpTypes.ScalpImageItemEnum.SENSITIVITY,
  TEMPERATURE = BeconScalpTypes.ScalpSensorItemEnum.TEMPERATURE,
  WATER = BeconScalpTypes.ScalpSensorItemEnum.WATER,
}
export type ComparisonV1SummaryItem = keyof typeof ComparisonV1SummaryItemEnum;

export type TAnalysisItem =
  | "SCALP_HEALTH_SCORE"
  | "SENSITIVITY"
  | "DEAD_SKIN"
  | "TEMPERATURE"
  | "WATER"
  | "HAIR_FOLLICLE_DENSITY"
  | "HAIR_QUANTITY";

export enum AnalysisItemEnum {
  SCALP_HEALTH_SCORE = "SCALP_HEALTH_SCORE",
  SENSITIVITY = "SENSITIVITY",
  DEAD_SKIN = "DEAD_SKIN",
  TEMPERATURE = "TEMPERATURE",
  WATER = "WATER",
  HAIR_FOLLICLE_DENSITY = "HAIR_FOLLICLE_DENSITY",
  HAIR_QUANTITY = "HAIR_QUANTITY",
}

export enum HairMacroImageTypeEnum {
  NORMAL = "NORMAL",
  MASKING = "MASKING",
}
export type HairMacroImageType = keyof typeof HairMacroImageTypeEnum;

export enum ScalpMicroSnakePositionEnum {
  forehead = "forehead",
  side_head = "side_head",
  hind_head = "hind_head",
  crown = "crown",
  forehead_center = "forehead_center",
  forehead_left = "forehead_left",
  forehead_right = "forehead_right",
  side_head_left = "side_head_left",
  side_head_right = "side_head_right",
}
export type ScalpMicroSnakePosition = keyof typeof ScalpMicroSnakePositionEnum;

const CrownMacroRatiosSchema = z.object({
  severe: z.number(),
  mild: z.number(),
  severe_average: z.number().optional(),
  mild_average: z.number().optional(),
});

export const CrownMacroDtoSchema = z.object({
  id: z.number(),
  source_images: z.nullable(
    z.object({
      normal: z.string(),
      masking: z.string(),
    }),
  ),
  ratio: CrownMacroRatiosSchema,
  simulation_result: z.array(
    z.object({
      simulation_count: z.number(),
      type: z.nativeEnum(HairMacroImageTypeEnum),
      image_url: z.string(),
    }),
  ),
  last_simulation_info: z
    .object({
      masking_image: z.string(),
      ratio: CrownMacroRatiosSchema.omit({ severe_average: true, mild_average: true }),
    })
    .nullable(),
});
export type CrownMacroDto = z.infer<typeof CrownMacroDtoSchema>;

const ForeheadImageUrlSchema = z.object({
  image_url: z.string().url(),
});

export const ForeheadMacroDtoSchema = z.object({
  normal: ForeheadImageUrlSchema,
  forehead: z.object({
    image_url: z.string(),
    my: z.number(),
    level: z.number(),
  }),
  aspect: z.object({
    image_url: z.string(),
    vertical: z.number(),
    level: z.number(),
  }),
  vertical: z.object({
    image_url: z.string(),
    upper: z.number(),
    lower: z.number(),
    level: z.number(),
  }),
  simulation: z.array(ForeheadImageUrlSchema),
});
export type ForeheadMacroDto = z.infer<typeof ForeheadMacroDtoSchema>;

export const GetScalpHistoryStatisticsRequestDtoSchema = z.object({
  accountType: z.nativeEnum(BeconAccountTypes.AccountTypeEnum),
  unit: z.nativeEnum(GroupUnitEnum),
  orderBy: z.nativeEnum(OrderByEnum).optional(),
  timeOffset: z.string(),
  skip: z.number().optional(),
  take: z.number().optional(),
});

export const GetScalpSetStatisticsRequestDtoSchema = z.object({
  orderBy: z.nativeEnum(OrderByEnum).optional(),
  timeOffset: z.string(),
  skip: z.number().optional(),
  take: z.number().optional(),
});

export const ScalpMicroTrendSchema = z.object({
  data: z.array(
    z.object({
      date: zDateString,
      score: z.number(),
    }),
  ),
  micro_position_type: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum),
});

export type ScalpMicroTrend = z.infer<typeof ScalpMicroTrendSchema>;

export const ScalpAverageScoreByMicroPositionSchema = z.object({
  image_data: z.array(
    z.object({
      normal_image: z.string(),
      uv_image: z.string(),
      my_score: z.number(),
    }),
  ),

  micro_position_type: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum),
  my_score: z.number().nullable(),
  total_average: z.number(),
});

export type ScalpAverageScoreByMicroPosition = z.infer<typeof ScalpAverageScoreByMicroPositionSchema>;

export const SkinAverageScoreByMicroMeasureItemSchema = z.object({
  micro_measure_item_type: z.nativeEnum(BeconScalpTypes.ScalpMeasureItemEnum),
  my_score: z.number(),
  total_average_score: z.number(),
});

export enum JobStatusEnum {
  WAITING = "WAITING",
  ANALYZING = "ANALYZING",
  COMPLETE = "COMPLETE",
  ERROR = "ERROR",
}

export type JobStatus = keyof typeof JobStatusEnum;

export const ScalpTypesDtoSchema = z.object({
  primary: z.nativeEnum(BeconUserTypes.ScalpTypeEnum),
  secondary: z.nativeEnum(BeconUserTypes.ScalpTypeEnum).nullable(),
});
export type ScalpTypesDto = z.infer<typeof ScalpTypesDtoSchema>;

export const ScalpTypeRatiosDtoSchema = z.object({
  age_group: z.string(),
  all_records: z.number(),
  ratios: z.array(
    z.object({
      scalp_type: z.nativeEnum(BeconUserTypes.ScalpTypeEnum),
      num_records: z.number(),
      ratio: z.number(),
    }),
  ),
});
export type ScalpTypeRatiosDto = z.infer<typeof ScalpTypeRatiosDtoSchema>;

export const ScalpHexagonScoreDtoSchema = z.object({
  hair_follicle_density: z.number(),
  hair_per_hair_follicle: z.number(),
  hair_quantity: z.number(),
  hair_thickness: z.number(),
  sensitivity: z.number(),
  dead_skin: z.number(),
  temperature: z.number(),
  water: z.number(),
  oil: z.number(),
  gas: z.number(),
});
export type ScalpHexagonScoreDto = z.infer<typeof ScalpHexagonScoreDtoSchema>;
export type ScalpHexagonScoreKey = keyof ScalpHexagonScoreDto;

export const ScalpDetailItemDtoSchema = z.object({
  item: z.nativeEnum(BeconScalpTypes.ScalpMeasureItemEnum),
  images: z.array(
    z.object({
      image_url: z.string(),
      position: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum),
    }),
  ),
  measurements: z.object({
    my: z.number(),
    all_averages: z.number(),
    my_average: z.number(),
    top_10: z.number(),
  }),
});

export type ScalpDetailItemDto = z.infer<typeof ScalpDetailItemDtoSchema>;

// # V1Advanced: Before&After
export const ComparisonV1AdvancedSummaryItemEnum = {
  SCALP_HEALTH_SCORE: "SCALP_HEALTH_SCORE",
  ...BeconScalpTypes.ScalpMeasureItemEnum,
} as const;
export type ComparisonV1AdvancedSummaryItem = keyof typeof ComparisonV1AdvancedSummaryItemEnum;

export const ComparisonSetResultV1AdvancedDtoSchema = z.object({
  summary_items: z.array(
    z.object({
      type: z.nativeEnum(ComparisonV1AdvancedSummaryItemEnum),
      before: z.number(),
      after: z.number(),
    }),
  ),
});
export type ComparisonSetResultV1AdvancedDto = z.infer<typeof ComparisonSetResultV1AdvancedDtoSchema>;

// # V2Advanced: precision-hair-loss
export const ComparisonSetResultV2AdvancedDtoSchema = z.object({
  hairloss_health_scores: z.object({
    my: z.number(),
    all: z.number(),
    top_10: z.number(),
  }),
  candidates: z.object({
    before: z.array(ComparisonCandidateDtoSchema),
    after: z.array(ComparisonCandidateDtoSchema),
  }),
});
export type ComparisonSetResultV2AdvancedDto = z.infer<typeof ComparisonSetResultV2AdvancedDtoSchema>;
